import { createContext, useContext, useState, useEffect, useMemo } from "react";
import logger from "../utils/logger";

// Create the cart context
const CartContext = createContext();

// Custom hook for using the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

// Cart provider component
export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage if available
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      try {
        return JSON.parse(savedCart);
      } catch (error) {
        logger.error("Error parsing saved cart:", error);
        return [];
      }
    }
    return [];
  });

  const [restaurant, setRestaurant] = useState(() => {
    const savedRestaurant = localStorage.getItem("restaurant");
    if (savedRestaurant) {
      try {
        return JSON.parse(savedRestaurant);
      } catch (error) {
        logger.error("Error parsing saved restaurant:", error);
        return null;
      }
    }
    return null;
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Save restaurant to localStorage whenever it changes
  useEffect(() => {
    if (restaurant) {
      localStorage.setItem("restaurant", JSON.stringify(restaurant));
    } else {
      localStorage.removeItem("restaurant");
    }
  }, [restaurant]);

  // Helper function to calculate Little Baker World bagel promotion
  const calculateLittleBakerPromotion = (cartItems, restaurant) => {
    if (!restaurant || restaurant.name !== "Little Baker World") {
      return { freeBagels: 0 };
    }

    // Count total bagels (all items are bagels for Little Baker World)
    const totalBagels = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    // Calculate free bagels (1 free for every 3 purchased)
    const freeBagels = Math.floor(totalBagels / 3);

    return { freeBagels };
  };

  // Calculate cart total without any promotion discounts
  const cartTotal = useMemo(() => {
    return cart.reduce((total, item) => {
      const itemPrice = item.discountPrice || item.price;
      return total + itemPrice * item.quantity;
    }, 0);
  }, [cart]);

  // Calculate promotion details for display
  const promotionDetails = useMemo(() => {
    return calculateLittleBakerPromotion(cart, restaurant);
  }, [cart, restaurant]);

  // Get total items in cart
  const cartItemCount = cart.reduce((count, item) => count + item.quantity, 0);

  // Add item to cart
  const addToCart = (item, restaurantData) => {
    // Check if restaurantData is provided
    if (!restaurantData) {
      logger.error("Restaurant data is required when adding items to cart");
      return;
    }

    // If adding an item from a different restaurant, clear the cart first
    if (restaurant && restaurantData._id !== restaurant._id) {
      if (cart.length > 0) {
        // Silently clear the cart without confirmation
        logger.log(
          `Switching cart from "${restaurant?.name}" to "${restaurantData.name}"`
        );
        setCart([]);
      }
      setRestaurant(restaurantData);
    } else if (!restaurant) {
      // First item in cart, set the restaurant
      logger.log(`Setting cart restaurant to "${restaurantData.name}"`);
      setRestaurant(restaurantData);
    }

    setCart((prevCart) => {
      // Check if item already exists in cart
      const existingItem = prevCart.find(
        (cartItem) => cartItem._id === item._id
      );

      if (existingItem) {
        // Update quantity if item already exists
        return prevCart.map((cartItem) =>
          cartItem._id === item._id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      } else {
        // Add new item with quantity 1
        return [...prevCart, { ...item, quantity: 1 }];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (itemId) => {
    setCart((prevCart) => {
      // Find the item
      const existingItem = prevCart.find((item) => item._id === itemId);

      if (existingItem && existingItem.quantity > 1) {
        // Decrease quantity if more than 1
        return prevCart.map((item) =>
          item._id === itemId ? { ...item, quantity: item.quantity - 1 } : item
        );
      } else {
        // Remove item completely if quantity is 1
        const newCart = prevCart.filter((item) => item._id !== itemId);

        // If cart is empty, clear restaurant data
        if (newCart.length === 0) {
          logger.log("Cart is now empty, clearing restaurant data");
          setRestaurant(null);
          localStorage.removeItem("restaurant");
        }
        return newCart;
      }
    });
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
    setRestaurant(null);
    localStorage.removeItem("restaurant");
    logger.log("Cart and restaurant data cleared");
  };

  // Get current restaurant information
  const getCurrentRestaurant = () => {
    return restaurant;
  };

  const value = {
    cart,
    restaurant,
    cartTotal,
    cartItemCount,
    promotionDetails,
    addToCart,
    removeFromCart,
    clearCart,
    getCurrentRestaurant,
    getCartSubtotal: () => {
      return cart.reduce((total, item) => {
        const itemPrice = item.discountPrice || item.price;
        return total + itemPrice * item.quantity;
      }, 0);
    },
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
